import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CalendarCheck } from "lucide-react";

export function PortalAcceptEstimate({ quote }) {
  const [dates, setDates] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [phone, setPhone] = useState(quote.phone || "");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/quote/availability?route=${quote.route}`)
      .then(r => setDates(r.data.available || []))
      .catch(() => {});
  }, [quote.route]);

  const submit = async () => {
    if (!startDate) { toast.error("Pick a preferred start date"); return; }
    if (!phone.trim()) { toast.error("A phone number is required to book"); return; }
    if (!consent) { toast.error("Please tick the cooling-off acknowledgement to continue"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/portal/quote/accept-estimate-and-book", {
        start_date: startDate, phone: phone.trim(), cooling_off_consent: true,
        origin: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete the acceptance — please try again or call the office");
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-5 space-y-3" data-testid="portal-accept-estimate">
      {quote.estimate_accepted_at && (
        <div className="bg-[#1F6B3B]/25 border border-[#2E8B57] px-3 py-2 text-[11px] text-[#CFF3DD]" data-testid="portal-accept-accepted-note">
          <CheckCircle2 size={13} className="inline mr-1 -mt-0.5" />
          You accepted this quote on {new Date(quote.estimate_accepted_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}. Complete your £499 payment below to lock in your date.
        </div>
      )}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#C79A3B] font-bold block mb-1">Preferred start date</label>
        <select data-testid="portal-accept-date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
          className="w-full bg-white text-[#12263A] border-0 px-3 py-2.5 text-sm">
          <option value="">Choose a date…</option>
          {dates.slice(0, 30).map(d => (
            <option key={d} value={d}>
              {new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "long", year: "numeric" })}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#C79A3B] font-bold block mb-1">Phone number</label>
        <input data-testid="portal-accept-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="Your best contact number" className="w-full bg-white text-[#12263A] border-0 px-3 py-2.5 text-sm" />
      </div>
      <label className="flex items-start gap-2 text-[11px] text-white/80 cursor-pointer">
        <input type="checkbox" data-testid="portal-accept-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[#C79A3B]" />
        <span>I agree work may begin within 14 days and acknowledge the deposit &amp; cancellation policy.</span>
      </label>
      <button data-testid="portal-accept-submit" onClick={submit} disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#1F6B3B] hover:bg-[#2E8B57] text-white font-bold text-sm px-4 py-3 transition-colors disabled:opacity-60">
        {submitting ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />}
        Accept revised quote — pay £499
      </button>
      <p className="text-[10px] text-white/60">The £499 booking fee reserves your date &amp; survey and comes off your total. Fully refundable within the cooling-off period.</p>
    </div>
  );
}
